import React, { useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button, { ButtonWithLoadingState } from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import {
  PlanInterval,
  PlanTier,
  PlanType,
} from '../../graphql/generated/graphql';
import { UIActions } from '../../ui/uiSlice';
import Link from '../../utils/router/Link';
import * as styles from './EditPlan.module.scss';
import PlanChooser from './PlanChooser';

const BILLING_SUMMARY_FRAGMENT = gql(`
  # eslint-disable-next-line @graphql-eslint/require-selections
  fragment EditPlan_billingSummary on BillingSummary {
    status
    # eslint-disable-next-line @graphql-eslint/require-selections
    plan {
      tier
      interval
      type
    }
    coupon {
      ...PlanChooser_billingCoupon
    }
  }
`);

const BILLING_PRICING_FRAGMENT = gql(`
  fragment EditPlan_billingPricing on BillingPricing {
    ...PlanChooser_billingPricing
  }
`);

interface EditPlanProps {
  billingSummary: DocumentType<typeof BILLING_SUMMARY_FRAGMENT>;
  billingPricing: DocumentType<typeof BILLING_PRICING_FRAGMENT>;
  initialTier: PlanTier | null;
  initialInterval: PlanInterval | null;
  updateBilling: (params: {
    planTier: PlanTier;
    planInterval: PlanInterval;
  }) => Promise<unknown>;
  onClose: () => void;
}

const EditPlan = ({
  billingSummary,
  billingPricing,
  initialTier,
  initialInterval,
  updateBilling,
  onClose,
}: EditPlanProps) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [planTier, setPlanTier] = useState<PlanTier>(
    initialTier ?? billingSummary.plan?.tier ?? PlanTier.Free,
  );
  const [planInterval, setPlanInterval] = useState<PlanInterval>(
    initialInterval ?? billingSummary.plan?.interval ?? PlanInterval.Monthly,
  );

  const onPlanChange = (
    newPlanTier: PlanTier,
    newPlanInterval: PlanInterval,
  ) => {
    setPlanTier(newPlanTier);
    setPlanInterval(newPlanInterval);
  };

  const handleSubmit = () => {
    const updatePlan = () => {
      setIsLoading(true);
      return updateBilling({ planTier, planInterval }).finally(onClose);
    };

    if (billingSummary.plan?.type === PlanType.Legacy) {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Change plan?',
          description:
            'Your team is currently on a plan that we no longer offer. You will be unable to switch back to this plan after you make this change.',
          cancelButtonText: 'Go back',
          confirmButton: {
            text: 'Confirm',
            onClick: updatePlan,
          },
        }),
      );
    } else {
      updatePlan();
    }
  };

  if (billingSummary.plan?.type === PlanType.Custom) {
    return (
      <div className={styles.subscribedMessage}>
        You are currently subscribed to a custom plan. Please contact{' '}
        <Link to="mailto:support@slab.com">support@slab.com</Link> to make any
        changes.
      </div>
    );
  }

  return (
    <>
      <PlanChooser
        selectedTier={planTier}
        selectedInterval={planInterval}
        onChange={onPlanChange}
        status={billingSummary.status}
        billingPricing={billingPricing}
        billingCoupon={billingSummary.coupon}
      />
      <div className={styles.footer}>
        <ButtonWithLoadingState
          variant="primary"
          loading={isLoading}
          onClick={handleSubmit}
        >
          Save
        </ButtonWithLoadingState>
        <Button
          className={styles.cancelButton}
          variant="secondary"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </>
  );
};

export default EditPlan;
